import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { blogService } from '../services/blogService';
import Seo from '../components/Seo';
import { estimateReadingTime, formatBlogDate, buildSeoMeta, stripHtml } from '../utils/blogHelpers';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const data = await blogService.getPublishedPostBySlug(slug);
        if (!isMounted) return;
        if (!data) {
          setNotFound(true);
          setPost(null);
          return;
        }
        setPost(data);

        const all = await blogService.getPublishedPosts();
        const related = all
          .filter((p) => p.id !== data.id)
          .filter((p) => p.categoryName === data.categoryName || (p.tags || []).some((t) => (data.tags || []).includes(t)))
          .slice(0, 3);
        if (isMounted) setRelatedPosts(related);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'article :', error);
        if (isMounted) setNotFound(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="container">
        <div className="loading-container">
          <div className="loading-spinner"><div className="spinner"></div></div>
          <p>Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="not-found">
        <div className="container">
          <div className="not-found-content">
            <i className="fas fa-exclamation-triangle"></i>
            <h1>404 - Article introuvable</h1>
            <p>Cet article n'existe pas, n'est plus disponible ou n'a pas encore été publié.</p>
            <Link to="/blog" className="btn btn-primary">
              <i className="fas fa-newspaper"></i> Retour au blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const seo = buildSeoMeta(post);
  const canonicalUrl = post.canonicalUrl?.trim() || `${window.location.origin}/blog/${post.slug}`;
  const publishedIso = post.publishedAt?.toDate ? post.publishedAt.toDate().toISOString() : (post.publishedAt ? new Date(post.publishedAt).toISOString() : undefined);
  const updatedIso = post.updatedAt?.toDate ? post.updatedAt.toDate().toISOString() : undefined;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: seo.description,
    image: seo.image ? [seo.image] : undefined,
    author: { '@type': 'Person', name: post.authorName || 'El Hiba Para' },
    datePublished: publishedIso,
    dateModified: updatedIso || publishedIso,
    mainEntityOfPage: canonicalUrl
  };

  const sanitizedContent = DOMPurify.sanitize(post.content || '');

  return (
    <div className="blog-post-page">
      <Seo
        title={seo.title}
        description={seo.description}
        canonicalUrl={canonicalUrl}
        image={seo.image}
        type="article"
        publishedTime={publishedIso}
        modifiedTime={updatedIso}
        jsonLd={jsonLd}
      />
      <div className="container">
        <div className="breadcrumb">
          <Link to="/blog"><i className="fas fa-arrow-left"></i> Retour au blog</Link>
        </div>

        <article className="blog-article">
          {post.featuredImageUrl && (
            <img
              src={post.featuredImageUrl}
              alt={post.featuredImageAlt || post.title}
              className="blog-article-image"
            />
          )}

          {post.categoryName && <span className="blog-card-category">{post.categoryName}</span>}

          <h1>{post.title}</h1>

          <div className="blog-article-meta">
            <span><i className="fas fa-user"></i> El Hiba Para</span>
            <span>
              <i className="fas fa-calendar"></i>{' '}
              <time dateTime={publishedIso}>{formatBlogDate(post.publishedAt)}</time>
            </span>
            <span><i className="fas fa-clock"></i> {estimateReadingTime(post.content)} min de lecture</span>
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="blog-article-tags">
              {post.tags.map((tag) => (
                <span key={tag} className="stock-badge in-stock">{tag}</span>
              ))}
            </div>
          )}

          <div
            className="blog-article-content"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        </article>

        {relatedPosts.length > 0 && (
          <section className="blog-related">
            <h2>Articles similaires</h2>
            <div className="blog-grid">
              {relatedPosts.map((related) => (
                <article key={related.id} className="blog-card">
                  <Link to={`/blog/${related.slug}`} className="blog-card-image-link">
                    <img
                      src={related.featuredImageUrl || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=500&h=300&fit=crop'}
                      alt={related.featuredImageAlt || related.title}
                      className="blog-card-image"
                    />
                  </Link>
                  <div className="blog-card-body">
                    <h3 className="blog-card-title">
                      <Link to={`/blog/${related.slug}`}>{related.title}</Link>
                    </h3>
                    <p className="blog-card-excerpt">{related.excerpt || stripHtml(related.content).slice(0, 100)}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
